import { useEffect, useState, useRef } from 'react';
import { clientLocalStorage } from '../services/storage/client-local-storage';
import type { StoryTrackingData } from '../services/tracking/storyEvent';
import { trackStoryEvent } from '../services/tracking/trackClientInfo';

export const useStoryTracking = (
  numericStoryId: string,
  currentPath: string,
  screenName: 'summary' | 'reader' | 'countdown',
  chapterNumber?: number,
  publisher?: StoryTrackingData['publisher'],
) => {
  const [shouldShowPopupForPath, setShouldShowPopupForPath] = useState(false);
  const processedPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if we've already processed this path
    if (processedPathRef.current === currentPath) {
      return;
    }
    const previousStoryId = clientLocalStorage.get('currentStoryId');
    const previousPath = clientLocalStorage.get('currentPath');

    // Check if previous path was a story route
    const wasInStoryRoute = previousPath && (previousPath.includes('/s/') || previousPath.includes('/countdown/'));

    // Fire Story Open Success if:
    // 1. No previous story (first visit)
    // 2. Coming from a different story
    // 3. Previous path was NOT a story route
    const shouldFireOpenEvent = !previousStoryId || previousStoryId !== numericStoryId || !wasInStoryRoute;

    if (shouldFireOpenEvent) {
      trackStoryEvent(
        'Story Open Success',
        { publisher },
        {
          seriesId: parseInt(numericStoryId, 10),
          episodeId: chapterNumber || 1,
          navigateResult: { state: 'success', nextScreen: screenName },
          navigateProgressType: 'open',
          needReplaceCurrentScreen: false,
          source: 'web',
        },
      );
    }

    // App install popup logic for chapter pages
    const isChapterPage = screenName === 'reader';
    if (isChapterPage) {
      const shouldShowPopup = !!previousPath && previousPath !== currentPath;
      setShouldShowPopupForPath(shouldShowPopup);
    }

    clientLocalStorage.save('currentStoryId', numericStoryId);
    clientLocalStorage.save('currentPath', currentPath);

    // Mark this path as processed
    processedPathRef.current = currentPath;
  }, [numericStoryId, currentPath, screenName, chapterNumber, publisher]);

  return {
    shouldShowPopupForPath,
  };
};
